import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import {
  Space,
  Text,
  Table,
  ActionIcon,
  Breadcrumbs,
  Anchor,
  Group,
  Select,
  useMantineTheme,
  Alert,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import useSegmentationBlocksHistoryApiService from "../../services/api/SegmentationBlocksHistoryApiService";
import { formatDateTimeISO } from "../../../../utils/DateTime";
import ProgressIndicator from "../../../../themes/components/ProgressIndicator";
import { ExportSegmentationBlocksHistoryButton } from "./index";

const VALID_ROWS_PER_PAGE = ["10", "25", "50"];
const DEFAULT_PAGE = 1;
const DEFAULT_ROWS_PER_PAGE = "10";

interface SegmentationBlocksHistoryContentProps {
  onBackToOverview?: () => void;
}

const SegmentationBlocksHistoryContent: React.FC<
  SegmentationBlocksHistoryContentProps
> = ({ onBackToOverview }) => {
  const theme = useMantineTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read pagination state from URL with validation and defaults
  const getPageFromUrl = useCallback((): number => {
    const pageParam = searchParams.get("page");
    if (!pageParam) return DEFAULT_PAGE;
    const parsed = parseInt(pageParam, 10);
    return Number.isNaN(parsed) || parsed < 1 ? DEFAULT_PAGE : parsed;
  }, [searchParams]);

  const getRowsPerPageFromUrl = useCallback((): string => {
    const rowsParam = searchParams.get("rows");
    if (rowsParam && VALID_ROWS_PER_PAGE.includes(rowsParam)) {
      return rowsParam;
    }
    return DEFAULT_ROWS_PER_PAGE;
  }, [searchParams]);

  const [page, setPageState] = useState(getPageFromUrl);
  const [rowsPerPage, setRowsPerPageState] = useState(getRowsPerPageFromUrl);

  // Sync state from URL on mount
  useEffect(() => {
    setPageState(getPageFromUrl());
    setRowsPerPageState(getRowsPerPageFromUrl());
  }, [getPageFromUrl, getRowsPerPageFromUrl]);

  // Update URL when pagination changes
  const setPage = useCallback(
    (newPage: number) => {
      setPageState(newPage);
      setSearchParams(
        (prev) => {
          prev.set("page", String(newPage));
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setRowsPerPage = useCallback(
    (newRowsPerPage: string) => {
      setRowsPerPageState(newRowsPerPage);
      setPageState(1); // Reset to page 1 when rows per page changes
      setSearchParams(
        (prev) => {
          prev.set("rows", newRowsPerPage);
          prev.set("page", "1");
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const pageSize = parseInt(rowsPerPage, 10);
  const { data, isLoading, isError } = useSegmentationBlocksHistoryApiService({
    page,
    page_size: pageSize,
  });

  const getOpacity = (isLastItem: boolean, isClickable: boolean): number => {
    if (isLastItem) return 1;
    if (isClickable) return 0.6;
    return 0.4;
  };

  const breadcrumbItems = [
    { title: "Overview", onClick: onBackToOverview },
    { title: "History Update Log", onClick: undefined },
  ].map((item, index, arr) => {
    const isLastItem = index === arr.length - 1;
    const isClickable = !!item.onClick;

    return (
      <Anchor
        key={item.title}
        onClick={(e) => {
          e.preventDefault();
          if (item.onClick) {
            item.onClick();
          }
        }}
        size="sm"
        aria-disabled={!isClickable && !isLastItem}
        style={{
          color: theme.black,
          opacity: getOpacity(isLastItem, isClickable),
          cursor: isLastItem || !isClickable ? "default" : "pointer",
          textDecoration: "none",
          pointerEvents: isClickable ? "auto" : "none",
        }}
      >
        {item.title}
      </Anchor>
    );
  });

  const formatTimestamp = (timestamp: string): string =>
    formatDateTimeISO(timestamp);

  if (isLoading) {
    return (
      <>
        <Space h="md" />
        <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
        <Space h="xl" />
        <ProgressIndicator />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Space h="md" />
        <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
        <Space h="xl" />
        <Alert icon={<AlertCircle size={16} />} title="Error" color="red">
          Failed to load history. Please try again later.
        </Alert>
      </>
    );
  }

  const results = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = data?.pages || 1;

  // Calculate display range
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  const handleRowsPerPageChange = (value: string | null): void => {
    if (value) {
      setRowsPerPage(value);
    }
  };

  return (
    <>
      <Space h="md" />
      <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
      <Space h="xl" />

      <Group position="apart" mb="md">
        <Text weight={600} size="lg">
          History Update Log
        </Text>
        <ExportSegmentationBlocksHistoryButton />
      </Group>

      {results.length === 0 ? (
        <Text color="dimmed" size="sm" align="center" py="xl">
          No history entries found.
        </Text>
      ) : (
        <Table verticalSpacing="md" striped>
          <thead>
            <tr>
              <th>
                <Text weight={600} size="sm">
                  Timestamp
                </Text>
              </th>
              <th>
                <Text weight={600} size="sm">
                  User
                </Text>
              </th>
              <th>
                <Text weight={600} size="sm">
                  Segment name
                </Text>
              </th>
              <th>
                <Text weight={600} size="sm">
                  Status update
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((entry) => (
              <tr key={entry.id}>
                <td>
                  <Text size="sm" color="dimmed">
                    {formatTimestamp(entry.timestamp)}
                  </Text>
                </td>
                <td>
                  <Text size="sm">{entry.actor_name}</Text>
                </td>
                <td>
                  <Text size="sm">{entry.segment_name}</Text>
                </td>
                <td>
                  <Text size="sm">{entry.action_label}</Text>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Space h="md" />
      <Group position="right" spacing="md">
        <Group spacing="xs">
          <Text size="sm" color="dimmed">
            Rows per page:
          </Text>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            data={[
              { value: "10", label: "10" },
              { value: "25", label: "25" },
              { value: "50", label: "50" },
            ]}
            size="xs"
            style={{ width: 70 }}
          />
        </Group>
        <Text size="sm" color="dimmed">
          {startItem}-{endItem} of {totalCount}
        </Text>
        <Group spacing="xs">
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            <ChevronsLeft size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
          >
            <ChevronsRight size={14} />
          </ActionIcon>
        </Group>
      </Group>
    </>
  );
};

export default SegmentationBlocksHistoryContent;
